// src/components/common/ProfileMenu.js
// Maxula
// Avatar rond cliquable (topbar) + menu déroulant. Placé AVANT la cloche de notifications.
// scope = 'admin' | 'merchant'

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiGrid, FiUser, FiSettings, FiLogOut } from 'react-icons/fi';
import { getAvatarUrl, getDisplayName, avatarOnError } from '../../utils/avatar';

const SCOPES = {
  admin: {
    role: 'Administrateur',
    links: [
      { label: 'Mon profil', to: '/admin/profile', icon: FiUser },
      { label: 'Tableau de bord', to: '/admin', icon: FiGrid },
      { label: 'Paramètres', to: '/admin/settings', icon: FiSettings }
    ]
  },
  merchant: {
    role: 'Espace marchand',
    links: [
      { label: 'Tableau de bord', to: '/merchant/dashboard', icon: FiGrid },
      { label: 'Mon profil', to: '/merchant/profile', icon: FiUser },
      { label: 'Paramètres', to: '/merchant/settings', icon: FiSettings }
    ]
  }
};

const ProfileMenu = ({ user, scope = 'merchant', onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cfg = SCOPES[scope] || SCOPES.merchant;
  const name = getDisplayName(user);
  const avatar = getAvatarUrl(user, { size: 80 });

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-100 transition-colors max-w-[230px]"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu du profil"
      >
        <img
          src={avatar}
          alt={name}
          onError={avatarOnError(user)}
          className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
        />
        <span className="hidden lg:flex flex-col items-start leading-tight min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate max-w-[140px]">
            {name}
          </span>
          <span className="text-[11px] text-gray-500 truncate max-w-[140px]">{cfg.role}</span>
        </span>
        <FiChevronDown
          className={`text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
          size={16}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
        >
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary-50 to-blue-50">
            <img
              src={avatar}
              alt={name}
              onError={avatarOnError(user)}
              className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || cfg.role}
              </p>
            </div>
          </div>

          <div className="py-1">
            {cfg.links.map((l) => (
              <Link
                key={`${l.label}-${l.to}`}
                to={l.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <l.icon className="text-gray-400" size={18} aria-hidden="true" />
                {l.label}
              </Link>
            ))}
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              if (typeof onLogout === 'function') onLogout();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 border-t border-gray-100 hover:bg-red-50 transition-colors"
          >
            <FiLogOut size={18} aria-hidden="true" />
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
